import type { ModelOption } from "./models";

export type EventType =
  | "run.queued"
  | "run.started"
  | "run.input"
  /** Live input: a message from the person handed to the running turn, read at its next step. */
  | "run.steered"
  /** A delegated run's one question to the agent that gave it the task, on both runs; and that agent's answer. */
  | "question.asked"
  | "question.answered"
  | "assistant.delta"
  | "assistant.message"
  | "tool.call"
  | "tool.result"
  | "delegation.created"
  | "proxy.call"
  | "message.created"
  | "run.finished"
  | "run.failed"
  | "run.cancelled"
  | "file.created"
  | "skill.read"
  | "workflow.updated"
  | "approval.updated"
  /** What the voice lab's GPT-Live side channel received, for the admin running it (voice slice 0, throwaway). */
  | "voice.lab"
  | "audit";

export type PlatformEvent<P = any> = {
  /** Empty string for events not tied to a run (e.g. audit). */
  runId: string;
  conversationId: string | null;
  /** Per-run sequence; 0 for transient events (assistant.delta) and audit. */
  seq: number;
  ts: string;
  type: EventType;
  payload: P;
};

export type RunStatus = "queued" | "running" | "succeeded" | "failed" | "cancelled";
export type RunMode = "chat" | "wait" | "background";
/**
 * `task` and `workflow` are history only: the task feature went on 2026-10-19 (migration 067), and a schedule's firing is
 * `schedule` since 070 (D12); rows before them carry the old words.
 */
export const RUN_TRIGGERS = ["unknown", "chat", "slack", "email", "meeting", "task", "workflow", "schedule", "delegation", "api_client", "wake", "trigger", "system"] as const;
/** What woke a run. Audit only: it grants nothing. Runs from before it was recorded say "unknown". */
export type RunTrigger = (typeof RUN_TRIGGERS)[number];

/** `task` is history only (see RUN_TRIGGERS): the comments and run results of the task threads that stayed. */
export const MESSAGE_CHANNELS = ["web", "slack_dm", "slack_thread", "email", "meeting", "delegation", "task", "schedule", "api", "wake", "platform"] as const;
/** The door a message came through, and the one a reply to it goes back out of. */
export type MessageChannel = (typeof MESSAGE_CHANNELS)[number];

/** What an agent and a person are shown for each channel. */
export const CHANNEL_LABEL: Record<MessageChannel, string> = {
  web: "Web chat", slack_dm: "Slack DM", slack_thread: "Slack thread", email: "Email", meeting: "Meeting",
  delegation: "Delegation", task: "Task", schedule: "Schedule", api: "API", wake: "Wake", platform: "AgentOS",
};

/**
 * What anyone is told while a Claude plan's usage limit is spent. Nothing here asks for a login: the login is good, the
 * window simply has to reset, and the platform starts the waiting turns itself when it does.
 */
export const CLAUDE_LIMIT = {
  /** The line a waiting person is told before the platform knows when the window resets. */
  waitingUnknown: "AgentOS has used up its Claude usage limit for now. Your message is saved and runs by itself when the limit resets.",
  /** The start of the line for a turn that had already acted when the limit hit, so it is not run again. */
  stopped: "AgentOS used up its Claude usage limit part-way through this one, so it stopped. Send it again",
} as const;

/** A reset time in UTC, because a stored line is read wherever it is read: "18:30 UTC". */
export const resetClock = (resetsAt: string) => `${new Date(resetsAt).toISOString().slice(11, 16)} UTC`;

/** The same, with how long that is from now: "18:30 UTC (in about 25 minutes)". */
export function resetWording(resetsAt: string, now: number = Date.now()): string {
  const minutes = Math.max(0, Math.round((Date.parse(resetsAt) - now) / 60_000));
  const hours = Math.round(minutes / 60);
  const left =
    minutes < 2 ? "in a moment" : minutes < 60 ? `in about ${minutes} minutes` : `in about ${hours} hour${hours === 1 ? "" : "s"}`;
  return `${resetClock(resetsAt)} (${left})`;
}

/** Why Claude cannot run right now, said in plain words: a login it refuses, or the plan's usage limit. */
/**
 * A person's own Claude (security plan D3): what they are told when it stops their runs, and what My connections
 * says. Anthropic's terms allow each person's own API key; a subscription login only where a deployment turns it on.
 */
export const CLAUDE_OWN = {
  /** A person's key was refused (or ran out of credit): their turns wait, everyone else's go on. */
  lapsed: "Anthropic refused your Claude key, so I can't reply yet. I've saved your message and will answer it the moment you reconnect it.",
  /** Anthropic asked this key to slow down: the turn waits and runs by itself. */
  limited: "Your Claude key is rate-limited by Anthropic for a moment. Your message is saved and runs by itself shortly.",
  /** Claude no longer accepts a person's sign-in: their turns wait until they sign in again. */
  signedOut: "Claude no longer accepts your sign-in, so I can't reply yet. I've saved your message and will answer it the moment you sign in again.",
  /** A person's Claude plan has spent its usage window: the turn waits for the reset and runs by itself. */
  planLimit: "Your Claude plan's usage limit is reached. Your message is saved and runs by itself when it resets.",
  /** Everyone brings their own Claude, and this person has not. */
  missing: "I can't reply until you connect your Claude account to AgentOS. I've saved your message and will answer it the moment you connect.",
  /** A turn that had already acted when Claude refused its login, so it is not run again. */
  stopped: "Claude refused the login part-way through this one, so it stopped. Check your Claude on My connections, then send it again.",
} as const;

/** A line that asks the person to fix their Claude ends with the link to Connections, where they do it: Slack and email have no top bar to find it in. */
export const withConnectionsLink = (line: string, publicUrl: string) => `${line} ${publicUrl.replace(/\/+$/, "")}/connections`;

/** The Claude row of My connections. */
export type ClaudeConnectionDTO = {
  status: "connected" | "lapsed" | "not_connected";
  /** `subscription`: signed in with Claude, through the binary's own login, in a Claude home that is theirs alone. */
  kind: "api_key" | "subscription" | null;
  detail: string | null;
  connectedAt: string | null;
  /** The Claude account a sign-in is, as the binary reports it. */
  account: string | null;
  /** Whether Sign in with Claude can start now (the replica that keeps Claude homes is connected). */
  signIn: boolean;
  /** A sign-in of theirs is waiting for its code. */
  signInWaiting: boolean;
  /** Whether a person's runs wait for their own Claude, rather than using the deployment's. */
  ownRequired: boolean;
};

/**
 * A subscription agents' own runs speak with (AIT-64, 107): signed in through its vendor's own program into its home on
 * the agents' login worker. The platform holds no secret of it: only that it is signed in, as whom, and how it is paced.
 */
export const MODEL_ACCOUNT_STATUSES = ["signed_out", "signed_in", "spent", "refused"] as const;
export type ModelAccountStatus = (typeof MODEL_ACCOUNT_STATUSES)[number];
/** What an agent's own run does when the subscription of its entry cannot take it: wait for it, or take the next entry. */
export const WHEN_SPENT = ["wait", "next"] as const;
export type WhenSpent = (typeof WHEN_SPENT)[number];

/** A vendor's window as it last reported it: how much of it is used (0–100) and when it starts again. */
export type ModelWindow = { utilization: number; resetsAt: string };
export type ModelAccountUsage = { at: string; fiveHour: ModelWindow | null; sevenDay: ModelWindow | null };

/** A sign-in waiting on the person: Anthropic's address and the code pasted back, or OpenAI's address and its one-time code. */
export type ModelSignIn = { kind: "code"; url: string } | { kind: "device"; url: string; code: string; expiresAt: string };

export type ModelAccountDTO = {
  id: string;
  vendor: "anthropic" | "openai";
  label: string;
  ownerUserId: string;
  ownerName: string;
  cap: number;
  paused: boolean;
  status: ModelAccountStatus;
  spentUntil: string | null;
  /** As which account it is signed in, as the binary reports it. */
  account: string | null;
  /** Runs on it now, against its cap. */
  running: number;
  usage: ModelAccountUsage | null;
  detail: string | null;
  /** Codex: new starts held for its renewal, which one run makes alone. */
  renewing: boolean;
  signIn: ModelSignIn | null;
  /** Agents whose model list names it: it is taken off them before it can be removed. */
  agents: { id: string; name: string }[];
  createdAt: string;
};

/** The program that runs a lab's models, as the workers report it. */
export type ModelDriverDTO = { name: string; versions: string[]; healthy: boolean; detail: string | null };

/** One block of Admin → Settings → Models. */
/**
 * A lab's company key on the Models page (AIT-119): only that one is set, by whom and when, never the key. `railway`:
 * the `ANTHROPIC_API_KEY` variable, which wins and is changed there.
 */
export type CompanyKeyDTO = {
  state: "not_set" | "set" | "railway";
  setBy: string | null;
  setAt: string | null;
  /** Anthropic refused it (AIT-129): since when and why in a few words. Nothing new runs on it until it is replaced or removed (on Railway, changed there). */
  refused: { since: string; detail: string } | null;
};

export type ModelLabDTO = {
  vendor: "anthropic" | "openai" | "xai";
  name: string;
  /** Null when its models are offered; otherwise why not. */
  unavailable: string | null;
  driver: ModelDriverDTO | null;
  models: { id: string; label: string; description: string }[];
  /** People signed in with their own (My connections); null where nobody can be. */
  peopleLogins: number | null;
  /** The company's key, as far as a screen may know it; null where runs never use one. */
  companyKey: CompanyKeyDTO | null;
  subscriptions: ModelAccountDTO[];
};

export type ModelsPageDTO = {
  labs: ModelLabDTO[];
  /** The agents' login worker, where subscriptions are signed in and kept, is connected. */
  signInReady: boolean;
};

/** One entry of an agent's model list for its own runs: a model and the subscription that pays for it. */
/** One entry of an agent's model list: a model and the subscription that pays for it. */
export type AgentModelEntryDTO = { model: string; accountId: string };

export type AgentModelsDTO = {
  entries: AgentModelEntryDTO[];
  whenSpent: WhenSpent;
  /** The subscriptions its owners own: the only ones an entry may name. */
  accounts: Pick<ModelAccountDTO, "id" | "vendor" | "label" | "ownerName" | "status" | "paused" | "account">[];
  /** The list is its owners' to change. */
  mayEdit: boolean;
};

export type ClaudeHold = {
  kind: "login" | "limit";
  /** When this outage started. */
  since: string;
  /** The words for why it holds, in a schedule's pause reason. */
  problem: string;
  /** The line a person is told once per conversation per outage. */
  waiting: string;
  /** How an earlier notice of this same outage is recognised: the waiting line starts with it. */
  waitingPrefix: string;
};

/** `rate_limit_event.rate_limit_info` from the Claude CLI's stream (claude 2.1.x): the fields we read. */
export type RateLimitInfo = {
  status: "allowed" | "allowed_warning" | "rejected";
  /** Unix seconds. */
  resetsAt?: number;
  /** `five_hour`, `seven_day`, `seven_day_opus`, `seven_day_sonnet`, `seven_day_overage_included`, `overage`. */
  rateLimitType?: string;
  utilization?: number;
  isUsingOverage?: boolean;
  unifiedWindows?: {
    five_hour?: { utilization: number; resetsAt: number };
    seven_day?: { utilization: number; resetsAt: number };
    seven_day_overage_included?: { utilization: number; resetsAt: number };
  };
};

/**
 * Why a run failed, in words a person reads, wherever a failed run is told to one: never the CLI's own subtype
 * (which is how "Run failed: success" reached Dimi's chat on 2026-10-12). An admin has the run itself for the raw
 * error — in the chat under the turn, on the run page.
 */
export function runFailureText(error: unknown): string {
  const e = (error ?? {}) as { reason?: string; window?: string; resetsAt?: string; acted?: boolean };
  switch (e.reason) {
    case "claude_limit": {
      const after = e.resetsAt ? ` after ${resetWording(e.resetsAt)}` : " once it resets";
      // Truthfully: a turn that had already done something stopped half-way; one that never started waited for
      // the window as long as it could and gave up.
      return e.acted === false
        ? `AgentOS could not get past its Claude usage limit, so this one never ran. Send it again${after}.`
        : `${CLAUDE_LIMIT.stopped}${after}.`;
    }
    case "claude_auth":
      return CLAUDE_OWN.stopped;
    case "codex_limit":
      return "This agent's OpenAI usage limit is spent, so this one stopped. Send it again once it resets.";
    case "codex_auth":
      return "AgentOS could not sign in to OpenAI for this one: Codex has no working login for this agent yet. An admin can see what happened on the run.";
    case "timeout":
      return "This one ran past its time limit and was stopped. Send it again, or ask for a smaller piece of it.";
    case "budget_reached":
      return "The agent has reached its monthly budget, so this one did not run. An admin can raise it on the agent's page.";
    case "agent_archived":
      return "This agent has been archived, so this one did not run.";
    case "error_max_turns":
      return "This one hit its limit of steps before it finished. Send it again, or ask for a smaller piece of it.";
    case "worker_lost":
      return "AgentOS could not finish this one: the machine it was running on went away. Send it again.";
    case "start_failed":
      return "AgentOS could not start this one. Send it again; an admin can see what happened on the run.";
    default:
      return "Something went wrong on this one and it stopped. Send it again; an admin can see what happened on the run.";
  }
}

/** The channel a run's messages belong to, from what woke it. Slack needs the thread to tell a DM from a channel. */
export const CHANNEL_OF_TRIGGER: Record<RunTrigger, MessageChannel> = {
  unknown: "platform", chat: "web", slack: "slack_dm", email: "email", meeting: "meeting", task: "task",
  workflow: "schedule", schedule: "schedule", delegation: "delegation", api_client: "api", wake: "wake", trigger: "api", system: "platform",
};

export type RunDTO = {
  id: string;
  conversationId: string;
  agentId: string;
  agentSlug: string;
  agentName: string;
  userId: string;
  parentRunId: string | null;
  depth: number;
  mode: RunMode;
  status: RunStatus;
  task: string;
  /** The scope this run works for (same agent, one object's memory and state); null when it has none. */
  scopeId: string | null;
  /** The agent version this run was started with; null for runs from before versions existed. */
  agentVersionId: string | null;
  /** The session this run spoke in; null until it starts. */
  sessionId: string | null;
  trigger: RunTrigger;
  /** Whose authority the run acts with: the person it works for, or the agent itself (work nobody asked for). */
  principal: "person" | "agent";
  /** The API client that woke it, when the trigger is api_client, or that started the chain a wake of it belongs to. */
  apiClientId: string | null;
  /** The schedule this run is a firing of, or null. */
  scheduleId: string | null;
  /** Material handed over with the request, given to the agent after the task as data. */
  context: string | null;
  result: string | null;
  error: unknown;
  /** This run's own cost at the model's list price: what its session's running total grew by during the run. */
  costUsd: number | null;
  inputTokens: number | null;
  outputTokens: number | null;
  /** This run's tokens by kind, recorded since 2026-10-22; null before. */
  usage: RunUsage | null;
  /** The model this run used: its agent's default, or the one chosen for the task it was handed. Null before 088. */
  model: string | null;
  /** Its agent's default model now, so a screen can say when the run's differed. */
  defaultModel: string | null;
  /** The subscription that paid for it (AIT-64): an agent's own run on its model list. */
  modelAccountId?: string | null;
  /** What paid for it (AIT-120), set as it starts. Null for a run that never started, and an old one that left no login and no cost. */
  paidBy: RunPaidBy | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  /** When a delegated run's result was handed to the run that collects it: once, by whichever way got there first. */
  resultReportedAt: string | null;
  /** The scope this run worked under (its subject), on the runs list only; null for a run under no scope. */
  subject?: { id: string; kind: string; slug: string; name: string } | null;
};

/** What paid for a run: an API key, billed for every token, or a subscription, billed for none of them. */
export type RunPaidBy = "api_key" | "subscription";

/**
 * A cost split by what paid for it (AIT-120). The two add up to the total beside them: `billedUsd` is what API keys
 * were charged, and `estimatedUsd` what runs on subscriptions would have cost on one, which nobody was billed.
 */
export type CostSplit = { billedUsd: number; estimatedUsd: number };

/** A turn's tokens by kind: new input, written to the prompt cache, read from it, and output. */
/** What a shell command is called in a run's log: Claude Code's Bash tool, or Codex's command (runner/drivers/codex.ts). */
export const SHELL_TOOLS: readonly string[] = ["Bash", "shell"];

export type RunUsage = {
  input: number; cacheWrite: number; cacheRead: number; output: number;
  /** Of cacheWrite, what was written at the one-hour lifetime (the rest at five minutes); and the model calls the turn made. */
  cacheWrite1h?: number; calls?: number;
  /**
   * Codex reports its session's running totals, not the turn's (runner/drivers/codex.ts): kept here as it said them —
   * `input` counting what was read from the cache too — so the session's next turn counts its own from them.
   */
  session?: { input: number; cacheRead: number; cacheWrite: number; output: number };
};

/** One delegated run still at work, as `pandas wait` reports it: read live, never from history. */
export type WaitChildDTO = {
  runId: string;
  agent: string;
  status: RunStatus;
  /** Since it started, or since it was queued while it has not. */
  elapsedS: number;
  /** What it is held behind: its turn in the queue, or the agents it is waiting on in turn. */
  waitingOn?: string;
  /** Its last words (the first 120 characters) or its last tool. */
  lastStep: string | null;
};

/**
 * What `pandas wait` answers: the first of the run's children to finish (claimed, so it is delivered once), the
 * person writing into the run, or the four minutes running out; each with where every other child stands.
 */
export type WaitAnswerDTO = {
  event: "finished" | "message" | "question" | "timeout";
  result?: { runId: string; agent: string; agentName: string; status: RunStatus; task: string; result?: string | null; error?: unknown };
  /** A child's one question, handed to its parent once: before any result, since the child is waiting on it. */
  question?: RunQuestionDTO;
  children: WaitChildDTO[];
};

/** One item of a delegated run's question to its parent: `pandas ask`'s shape plus what it does if nobody answers. */
export type ParentQuestionItem = {
  question: string;
  default: string;
  header?: string;
  multiSelect?: boolean;
  options?: { key: string; label: string; description?: string }[];
};

/** A delegated run's one question, as the agent that gave it the task is handed it. */
export type RunQuestionDTO = {
  id: string;
  child: string;
  agent: string;
  agentName: string;
  task: string;
  questions: ParentQuestionItem[];
  deadlineAt: string;
};

/** What `pandas ask-parent` comes back with: the parent's answer, or proceed on the defaults, or still waiting. */
export type AskParentAnswerDTO =
  | { id: string; answered: true; answer: string; by: string }
  | { id: string; answered: false; proceed: "defaults"; reason: string }
  | { id: string; answered: false; pending: true; deadlineAt: string };

/** What one conversation started and spent in the last 24 hours: watched, never enforced before the platform is live. */
export type ObservedDTO = CostSplit & {
  conversationId: string;
  runs: number;
  spendUsd: number;
  delegations: number;
  /** Its usual day: the 90th percentile of its own daily spend over 30 days, or the platform's when it has too little history. */
  usualUsd: number | null;
  usualFrom: "own" | "platform" | null;
  /** Past twice its usual: an audit row says so, and nothing is refused. */
  over: boolean;
};

/** One of the conversations that spent the most in the last 24 hours, for the admin's Live page. */
export type BusyConversationDTO = ObservedDTO & { title: string; agentName: string; userName: string | null };

/** How long an agent's delegated tasks usually take: its succeeded delegated runs over 30 days, from five of them. */
export type TypicalDurationDTO = { p50S: number; p90S: number; runs: number };

/** The shared Admin login used for token sign-in. No real person is behind it, so it owns no agent. */
export const ADMIN_LOGIN_EMAIL = "admin@pandas.local";

/** One person on an agent's lists: an owner (an admin who answers for it), or someone named to use it. */
export type AgentOwnerDTO = { userId: string; name: string };

/** Who answers for an agent: the admins named, or every admin — so a future admin becomes an owner by being made one. */
export const AGENT_OWNER_MODES = ["named", "all_admins"] as const;
export type AgentOwnerMode = (typeof AGENT_OWNER_MODES)[number];

/** Who may use an agent, on top of its owners, who always may: the people named, every admin, or everyone. */
export const AGENT_ACCESS_MODES = ["named", "all_admins", "everyone"] as const;
export type AgentAccessMode = (typeof AGENT_ACCESS_MODES)[number];

export type AgentDTO = {
  id: string;
  slug: string;
  name: string;
  description: string;
  systemPrompt: string;
  model: string;
  isPrimary: boolean;
  enabled: boolean;
  /** When it was archived: kept with its history, switched off, and left out of every list unless archived ones are asked for. */
  archivedAt: string | null;
  /** Its picture: an uploaded image, the panda for the primary agent, null for initials on a colour. */
  avatarUrl: string | null;
  sandboxAllowedDomains: string[];
  connectorIds: string[];
  /** Per own connector grant, the data level a run on the agent's own behalf gets ("default" or a level key). */
  connectorLevels: Record<string, string>;
  /** Sub-agents inherit their parent's connectors; their prompt names the parent (its name and description). */
  parentAgentId: string | null;
  /**
   * Who answers for it — its instructions, its skills for everyone, its shared memory, its goals and its requests:
   * the admins named in `owners`, or every admin. The named list is kept while the mode is `all_admins`, unread.
   */
  ownerMode: AgentOwnerMode;
  owners: AgentOwnerDTO[];
  /** Who answers for it now, by id: the named owners, or every admin "all admins" means. What screens test ownership by. */
  ownerIds: string[];
  /** Who may use it, its owners always included: the people named in `access`, every admin, or everyone. */
  accessMode: AgentAccessMode;
  access: AgentOwnerDTO[];
  /** Runs stop starting once this month's spend reaches it; null = no cap. */
  monthlyBudgetUsd: number | null;
  /** Instructions and model are versioned; this is the edition that runs now. */
  version: number;
  currentVersionId: string | null;
  /** The scope types this agent has access to: it works under, and may create, scopes of these types only. */
  scopeTypes: string[];
  /** Whether it has access to any scope type; without one it always works in its default scope. */
  scoped: boolean;
  defaultScopeId: string;
  /** Off: its runs are never offered delegation and are refused if they try. Per agent, so off for every scope. */
  canDelegate: boolean;
  /** On: an agent handing it a task may choose the model for that task. Off: it always runs on its default `model`. */
  modelPerTask: boolean;
  /**
   * On: the people this agent works for keep skills of their own on it, which only their own runs of it see.
   * Off (a specialist): nobody may write one, and none already written is offered.
   */
  peopleCanAddSkills: boolean;
  /** The review templates this agent may file, granted in its settings the way connectors are. */
  reviewTemplates: string[];
  /**
   * Whether the person asking is one of its owners (named, or an admin when all admins own it), so a screen offers
   * the operation of an agent — instructions, skills for everyone, shared memory, goals — only to who may change it.
   * Only on the agent list and the agent's own read.
   */
  youAnswerFor?: boolean;
  /** Whether the person asking may use it (its owners, and whoever its access mode lets in). Same places. */
  youCanUse?: boolean;
  createdAt: string;
  updatedAt: string;
};

/**
 * An agent's prompt exactly as its next run gets it (AIT-27): the system prompt, and the run block of sample runs built
 * by the same code from real records — the admin looking, in the web chat; for an agent with a scope type, one wake on
 * a scope it works on too.
 */
export type PromptPreviewDTO = {
  system: string;
  chars: number;
  /** The platform's estimate (PROMPT_CHARS_PER_TOKEN). */
  tokens: number;
  samples: { label: string; scopeKey: string | null; run: string }[];
};

/** One edition of an agent's instructions and model. Appended on every edit, never changed or deleted. */
export type AgentVersionDTO = {
  id: string;
  agentId: string;
  version: number;
  systemPrompt: string;
  model: string;
  /** user:<id>, agent:<run id>, system or migration:<n>. */
  createdBy: string;
  createdAt: string;
};

/**
 * One of an agent's own wakes in one scope: at runAt it is started again on its own behalf with this prompt. Only a run on
 * an agent's own behalf books one (D6); up to 10 wait per scope, and a key names one so booking it again replaces it.
 */
export type WakeDTO = {
  id: string;
  agentId: string;
  scopeId: string;
  scopeKey: string;
  key: string | null;
  runAt: string;
  prompt: string;
  createdByRunId: string | null;
  firedRunId: string | null;
  createdAt: string;
};

/** A condition on a trigger's event: the value at `path` (dots, e.g. data.automated) equals this one exactly. */
export type TriggerCondition = { path: string; equals: string | number | boolean | null };
/** Where a trigger finds its scope in the event: the id at `path` in the tool `tool`, of scope type `type`; named from `namePath` when one is made. */
export type TriggerScopeRule = { type: string; tool: string; path: string; namePath?: string | null };

/**
 * One of an agent's triggers (AIT-45): an address another app sends signed events to (Standard Webhooks). Each event of
 * its types that meets its conditions starts one run of the agent on its own behalf, with the trigger's fixed prompt and
 * the event as material, under the scope its rule finds, or none. Its owners manage it; the secret is shown once.
 */
export type TriggerDTO = {
  id: string;
  agentId: string;
  name: string;
  eventTypes: string[];
  conditions: TriggerCondition[];
  scopeRule: TriggerScopeRule | null;
  prompt: string;
  enabled: boolean;
  /** POST here, unsigned by any session: the signature is the only way in. */
  url: string;
  secretRotatedAt: string;
  /** Its secret was made in a session a named token opened, and stops working when that token does. */
  secretTiedToToken: boolean;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** How many events it received in all, and when the last one came. */
  eventCount: number;
  lastEventAt: string | null;
};

export const TRIGGER_OUTCOMES = ["started", "duplicate", "ignored", "refused", "failed"] as const;
export type TriggerOutcome = (typeof TRIGGER_OUTCOMES)[number];

/** One call a trigger received and what it did. The body is never kept: the run it started has it. */
export type TriggerEventDTO = {
  id: string;
  triggerId: string;
  eventId: string;
  type: string | null;
  source: string | null;
  outcome: TriggerOutcome;
  reason: string | null;
  runId: string | null;
  scopeKey: string | null;
  scopeName: string | null;
  receivedAt: string;
};

/** An outside system that wakes agents through POST /api/v1/runs. Its key is shown once; only the prefix stays in the open. */
export type ApiClientDTO = {
  id: string;
  name: string;
  keyPrefix: string;
  enabled: boolean;
  lastUsedAt: string | null;
  createdBy: string | null;
  /** Which agents it may wake, and in which scopes: "*" (every scope), "<kind>:*" (one kind of key) or one exact key. */
  agents: { agentId: string; agentSlug: string; agentName: string; scopePattern: string }[];
  createdAt: string;
  updatedAt: string;
};

/** One agent working under one scope (a scoped agent): its thread, its wake, its working folder, one run at a time. Keyed by the scope's key, or "default". */
export type ScopeDTO = {
  id: string;
  agentId: string;
  key: string;
  displayName: string;
  /** The subject this scope is about; null for the default scope, which is about nothing. */
  subjectId: string | null;
  state: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

/**
 * A scope: <type>:<id>, such as account:<uuid>. One row shared by every agent working under it, with its id in each
 * tool. It holds memory, skills and files of its own, which every agent working under it reads.
 */
export type SubjectDTO = {
  id: string;
  /** The scope type's key. */
  kind: string;
  /** Immutable; the id part of the key. A new scope's is its own uuid. */
  slug: string;
  /** kind:slug */
  key: string;
  displayName: string;
  /** The subject's id in each outside system, e.g. { salesos: "<account uuid>", attio: "<record id>" }. */
  links: Record<string, string>;
  /** How many agents work under it. */
  scopeCount: number;
  /** When it was archived: kept and readable, but no new work starts under it. Null while it is live. */
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

/** One agent working under a scope, as the scope's page lists it: its thread and its latest run there. */
export type ScopeAgentDTO = {
  id: string; agentId: string; agentSlug: string; agentName: string; key: string;
  conversationId: string | null; runCount: number; lastRunAt: string | null; lastRunStatus: string | null;
};

/** One scope an agent works under, as the agent page's Scopes tab lists it. */
export type AgentScopeDTO = {
  id: string; key: string; subjectId: string; kind: string; displayName: string;
  conversationId: string | null; runCount: number; lastRunAt: string | null; lastRunStatus: string | null; createdAt: string;
  /** Where it stands (AIT-23): the last run's one-line answer (or its error), its pending requests by kind, and its next wake. */
  lastRunAnswer: string | null;
  pending: Record<string, number>;
  nextWake: { at: string; key: string | null } | null;
};

/**
 * One of an agent's own scopes as its agent level reads it (`pandas scope status`): the answer line only when the last
 * run worked on the agent's own behalf or for the same person as the reader, the next wake's time and key, never its prompt.
 */
export type ScopeStatusDTO = {
  key: string; name: string;
  lastRun: { at: string; status: string; answer: string | null; answerHidden?: true } | null;
  pending: Record<string, number>;
  nextWake: { at: string; key: string | null } | null;
};

/** A scope type (account, deal): the tools its scopes have an id in, and the agents with access to it. */
export type SubjectTypeDTO = {
  key: string;
  name: string;
  /** Its plural ("Accounts"), lower-cased in the words its agents read: "all your accounts". */
  pluralName: string;
  /** What one of it is. Every agent working per this type reads it in its system prompt. */
  description: string;
  /** The tools its scopes have an id in, main tool first, with what the id is called there (salesos → account_id). */
  fields: { system: string; idField: string }[];
  /** Agents that may work under, and create, scopes of this type. */
  agentIds: string[];
  /** Its live scopes; archived ones are counted apart. */
  scopeCount: number;
  archivedCount: number;
  /** When it was archived, with its scopes: no new scope of it is made. Null while it is live. */
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AuthType = "header" | "bearer" | "basic" | "oauth2" | "github_app" | "google_service_account";

/**
 * An action a person has to have approved before it may happen. The proxy forwards a call falling under one of these
 * only with an approved, unused review request of the named template behind it, whose fields equal the call's word
 * for word; `needs: "released"` is the other half of the pair — a call allowed only on something already released.
 *
 * With no template (AIT-108) a rule works for any tool with no code of its own: `needs: "approval"` refuses the call,
 * the agent proposes the exact call (`pandas propose action`), and the platform sends those bytes once at the addressee's
 * yes; `needs: "never"` refuses it outright, with no way to propose. These two shapes are the only ones the Tool
 * permissions tab writes. A third, `needs: "person"` (AIT-267), has no card: the call goes through only with a person
 * behind the run, and is refused on a schedule, a trigger or an agent working on its own.
 */
export type ConnectorProtectedRule = {
  /** "POST"; a comma-separated list or `*`, as in `allow`. */
  method: string;
  /** Path glob, as in `allow`. With `needs: "released"`, exactly one segment is `*`: where the upstream id sits. */
  path: string;
  /** The review template an approved request must be of. Absent on an action rule. */
  template?: string;
  /**
   * What the call needs. `"approval"` (the default on a template): with a template, an approved, unused review of this
   * agent whose fields equal the call's; with none, an approved `action` request holding this very call. `"released"`:
   * the id in its path must be one this agent already released from such a review. `"never"`: nothing lets it through.
   * `"person"` (action rules only): nothing to approve, but a person must be behind the run (`personIsBehind`).
   */
  needs?: "approval" | "released" | "never" | "person";
  /** With a template's `"approval"`: a field path into the call's JSON body for every review field that must equal it. */
  match?: Record<string, string>;
  /** On an action rule, required: what the action is called, in the refusal, on the card and in `pandas connectors`. */
  label?: string;
  /** With an action's `"approval"`: how the card reads the stored body. `"json"` when left out. */
  display?: ActionDisplay;
  /** With an action's `"approval"`: a person's schedule asks too, instead of passing with nobody there to approve. */
  askOnSchedules?: boolean;
};

/**
 * One action of a connector's imported list (`src/server/connector-actions/<key>.json`, `scripts/import-connector-actions.ts`).
 * `paths` holds a call's every form, with `*` for one segment and `**` for a value that may hold slashes. `name` may be a
 * vendor's words and is only ever drawn as plain text; `label` is ours, and is what a rule on it may be called.
 */
export type ListedAction = { method: string; paths: string[]; name: string; area: string; readOnly: boolean; label?: string; display?: ActionDisplay };

/** What one action on a connector's Tool permissions tab does: no rule, a person's approval, or never. */
export type ConnectorActionState = "allow" | "approval" | "never";

/**
 * Why a row can't be set there: the connector's own rules refuse the call, a template's card is its way out, or another
 * rule (a wider path, a level's) holds it, named in `by`.
 */
export type ConnectorActionLock = { kind: "refused" | "template" | "released" | "rule" | "person"; by?: string };

/** One action as `GET /api/connectors/:id/actions` draws it. `added` is a rule the tool's list has no row for: removing it takes the row away. */
export type ConnectorActionRow = {
  method: string;
  paths: string[];
  name: string;
  area: string;
  readOnly: boolean;
  state: ConnectorActionState;
  askOnSchedules: boolean;
  lock: ConnectorActionLock | null;
  added: boolean;
};

export type ConnectorActionsDTO = {
  /** The one address a tool takes everything at (Linear's `POST /graphql`), when it has one: its actions can't be told apart. */
  oneAddress: string | null;
  actions: ConnectorActionRow[];
};

/** One change the tab saves. `name` and `display` are for an action that is on no list. */
export type ConnectorActionChange = { method: string; paths: string[]; state: ConnectorActionState; askOnSchedules?: boolean; name?: string; display?: ActionDisplay };

/** How a card reads a call's body. A format, never a tool: `email-raw` is base64url MIME in a JSON `raw` field. */
export const ACTION_DISPLAYS = ["json", "email-raw"] as const;
export type ActionDisplay = (typeof ACTION_DISPLAYS)[number];

/** Enforced by the connector proxy on every call. */
export type ConnectorPolicy = {
  /** Block POST/PUT/PATCH/DELETE. */
  readOnly?: boolean;
  /** When set, only these "METHOD /path" rules are allowed (`*` = one segment, `**` = any). */
  allow?: string[];
  /** Always refused at this level, checked before anything else. Methods may be comma separated: "PUT,DELETE /path". */
  deny?: string[];
  /** JSON keys whose values are replaced before agents (and logs) see the response. */
  redactFields?: string[];
  /** Calls that need a person's approval behind them, or are never made, checked after the rules above and before anything is forwarded. */
  protected?: ConnectorProtectedRule[];
};

/** A named set of rules that admins assign to specific users (e.g. "Full access" for HR). */
export type ConnectorAccessLevel = {
  key: string;
  name: string;
  policy: ConnectorPolicy;
  /** Platform admins get this level without an explicit assignment. */
  autoAssignAdmins?: boolean;
};

export const DEFAULT_ACCESS_LEVEL = "default";

export type ConnectorDTO = {
  /** On the answer to an update alone: the stored key was cleared because the address or the auth changed. */
  secretsCleared?: boolean;
  /** On the list of connectors alone: what the platform itself uses it for now, by the rules that pick it (AIT-118). */
  platformUses?: string[];
  /** The default level: applies to every user without an assigned level. */
  policy: ConnectorPolicy;
  levels: ConnectorAccessLevel[];
  id: string;
  slug: string;
  name: string;
  baseUrl: string;
  authType: AuthType;
  authConfig: Record<string, unknown>;
  usageGuide: string;
  enabled: boolean;
  needsReconnect: boolean;
  hasSecrets: boolean;
  /** Tell the tool behind it which agent, run and scope is calling (X-Agent, X-Run, X-Scope), in the platform's words. Off for tools that need none of our ids. */
  sendProvenance: boolean;
  /** Whose login a call uses: the connector's one shared key, the run's identity's own (D2 slices 3–4), or AgentOS's pass (an internal tool). */
  credentials: ConnectorCredentialsMode;
  /** An internal tool's audience: the `aud` of every pass made for it, the origin it checks passes against. Null on any other. */
  audience: string | null;
  /** Agents reach it only with a grant of their own: the primary agent, which has every other connector, has no automatic reach to this one (AIT-267). */
  agentGrantRequired: boolean;
  /** The personal sign-in's app, registered once in the tool's console: its client id (not a secret), and whether its secret is stored. */
  signInClientId: string | null;
  signInReady: boolean;
  /** What the workspace key is: one pasted and stored (`key`), or the sign-in app itself, acting as the app (AIT-57). */
  workspaceMode: WorkspaceMode;
  /** The sign-in app's own token, when it holds one: when it lapses. The platform renews it a day before. */
  workspaceTokenExpiresAt: string | null;
  createdAt: string;
  updatedAt: string;
};

/** `key`: a workspace key an admin pasted. `signin_app`: the sign-in app's own token (client credentials), no person's. */
export type WorkspaceMode = "key" | "signin_app";
export const WORKSPACE_MODES: WorkspaceMode[] = ["key", "signin_app"];

/** Whether agents working on their own have a workspace key to use: one stored, or the sign-in app itself. */
export const hasWorkspaceKey = (c: Pick<ConnectorDTO, "hasSecrets" | "workspaceMode">) => c.hasSecrets || c.workspaceMode === "signin_app";

/**
 * `shared`: everyone uses the workspace key, at their access level. `own`: people use their own login (a sign-in or a
 * token, whatever the tool declares), never the workspace key; agents running on their own still use the workspace key at
 * the agent's level (security plan D2, "Connector model — DECIDED 2026-10-19"). `internal`: Pandas's own tool, which
 * people are already connected to — no key and no login: AgentOS vouches for each call with a signed pass (2026-10-23).
 */
export type ConnectorCredentialsMode = "shared" | "own" | "internal";
export const CONNECTOR_CREDENTIAL_MODES: ConnectorCredentialsMode[] = ["shared", "own", "internal"];
/** The auth types a connector may use each identity's own login with: a personal key, or a per-person OAuth login. */
export const OWN_LOGIN_AUTH_TYPES: AuthType[] = ["bearer", "header", "basic", "oauth2"];

export type CredentialStatus = "connected" | "lapsed";

/** One row of My connections: a tool this person may use, and how. */
export type ConnectionDTO = {
  connectorId: string;
  slug: string;
  name: string;
  /** Needs you: their own login is wanted and missing or lapsed. Connected: theirs works. Provided: the workspace key serves them. */
  group: "needs_you" | "connected" | "provided";
  /** `key` / `oauth`: the kind of their own login; `impersonation`: the platform acts as them (Google Workspace); `workspace`: the workspace key; `internal`: Pandas's own tool, connected through AgentOS's pass. */
  method: "key" | "oauth" | "impersonation" | "workspace" | "internal";
  status: CredentialStatus | "not_connected" | "impersonated" | "provided";
  /** Whether they can sign in at the tool here: declared by the tool and its sign-in app set up. */
  signIn: boolean;
  /** A token of their own they can paste instead, with the tool's own steps; null when the tool has none. */
  token: { label: string; placeholder?: string; steps: string } | null;
  /** Why a login lapsed, or why there is no way to connect yet, in words to act on. */
  detail: string | null;
  connectedAt: string | null;
  /** The account their login acts as, when the tool says. */
  account: string | null;
  lastUsedAt: string | null;
  /** Provided: what the workspace key lets them do there — "Read-only", "Read and write", or the level's own name. */
  access: string | null;
  /** A catalog key, for the tool's logo. */
  catalog: string | null;
};

/** What an access level lets a caller do, in three words a person reads: nothing changes, anything goes, or rules of its own. */
export type AccessCharacter = "Read-only" | "Read and write" | "Custom";

/** Read-only: no writes, or only GET and HEAD rules. Read and write: no rule at all. Sends needing approval are not access. */
export function accessCharacter(policy: ConnectorPolicy | undefined): AccessCharacter {
  const p = policy ?? {};
  if (p.readOnly) return "Read-only";
  const ruled = Boolean(p.allow?.length || p.deny?.length || p.redactFields?.length);
  if (!ruled) return "Read and write";
  // A read: GET or HEAD, or a POST to a query or search endpoint (Attio's records/query, HubSpot's search) — REST's
  // convention for a read with a body. A label for people; the policy itself is what the proxy enforces.
  const reads = (rule: string) => {
    const [methods = "", path = ""] = rule.trim().split(/\s+/);
    const last = path.split("/").filter(Boolean).at(-1) ?? "";
    return methods.split(",").every((m) => m === "GET" || m === "HEAD" || (m === "POST" && (last === "query" || last === "search")));
  };
  if (p.allow?.length && p.allow.every(reads) && !p.redactFields?.length) return "Read-only";
  return "Custom";
}

/**
 * The connector's rule in one plain sentence, generated from how it is set up: who uses which login, and at what access.
 * Four ways a connector can stand: the workspace key only, the key and people's own login, people's own login only, or
 * neither yet. `agentAccess`: what agents working on their own get ("Read-only", …, or null when they differ by agent);
 * `personalWay`: whether the tool declares a way in for people at all.
 */
export function connectorRule(
  c: { name: string; authType: AuthType; credentials: ConnectorCredentialsMode; hasSecrets: boolean; workspaceMode?: WorkspaceMode },
  opts: { agentAccess: string | null; personalReady: boolean; personalWay?: boolean },
): string {
  if (c.authType === "google_service_account") return `Agents act as the person asking, with that person's own ${c.name} access.`;
  if (c.credentials === "internal") {
    return `Everyone with ${c.name} is connected through Pandas: AgentOS vouches for each call with a signed pass naming the person the run works for, or the agent working on its own.`;
  }
  const level = `at their level${opts.agentAccess && opts.agentAccess !== "Custom" ? ` (${opts.agentAccess.toLowerCase()})` : ""}`;
  const agents = c.workspaceMode === "signin_app"
    ? ` Agents working on their own use the sign-in app itself, which only reads ${c.name}, ${level}.`
    : c.hasSecrets
      ? ` Agents working on their own use the workspace key ${level}.`
      : ` Agents working on their own cannot use ${c.name}: there is no workspace key.`;
  if (c.credentials === "own") {
    return `People's runs use their own ${c.name} login only${opts.personalReady ? "" : ", and nobody can connect yet: set up a way in below"}.${agents}`;
  }
  if (!c.hasSecrets) return `Nobody can use ${c.name} yet: store a workspace key${opts.personalWay ? ", or turn on people's own login" : ""}.`;
  return `People's runs use the workspace key at each person's level.${agents}`;
}

/**
 * Which of the four a conversation is. A conversation is owner + parties + scope:
 * direct = one person with one agent, across every channel; agent = an agent's own thread;
 * work = history only: a run of an old step-based schedule or a task thread (nothing makes one since 2026-10-19);
 * group = a Slack channel thread, a meeting, an email with several people.
 */
export const CONVERSATION_KINDS = ["direct", "agent", "work", "group"] as const;
export type ConversationKind = (typeof CONVERSATION_KINDS)[number];

export type ConversationDTO = {
  id: string;
  userId: string;
  agentId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  /** Where the conversation happens: the web app or a Slack thread/DM. */
  source?: "web" | "slack";
  slackChannel?: string | null;
  kind: ConversationKind;
  /** An earlier direct conversation points at the live one, which its messages were folded into. */
  supersededBy?: string | null;
  /** When its messages were folded into the live conversation. Set means a link to it redirects there. */
  mergedAt?: string | null;
  /** The meeting that made it (its minutes, its live questions, the bot's help): only that meeting's members read it (AIT-335). */
  meetingId?: string | null;
};

/** A channel account proven to belong to one person. Every door finds the sender by (channel, external id). */
export const IDENTITY_CHANNELS = ["email", "slack", "github", "telegram", "whatsapp", "phone", "google"] as const;
export type IdentityChannel = (typeof IDENTITY_CHANNELS)[number];

/** How the platform knows an account is this person's. An identity is never typed in without proof. */
export const IDENTITY_PROOFS = ["signin", "slack_profile", "code", "migrated", "directory", "meet_participant"] as const;
export type IdentityProof = (typeof IDENTITY_PROOFS)[number];

export type IdentityDTO = {
  id: string;
  userId: string;
  channel: IdentityChannel;
  externalId: string;
  label: string | null;
  proof: IdentityProof;
  provenAt: string;
  lastSeenAt: string | null;
};

/** One row of the chat sidebar: an agent the person may talk to, and the one chat they have with it. */
export type ChatDTO = {
  agentId: string;
  slug: string;
  name: string;
  isPrimary: boolean;
  /** Its picture, as on AgentDTO. */
  avatarUrl: string | null;
  /** Null until they speak for the first time: the conversation is created by the first message. */
  conversationId: string | null;
  lastMessageAt: string | null;
  /** The person's own last message to it, from any door: what the rows are ranked by. */
  lastTalkedAt: string | null;
};

/** Just enough of an agent to draw it wherever it is mentioned: a card, a message, a run row. */
export type AgentAvatarDTO = { agentId: string; slug: string; name: string; avatarUrl: string | null };

/**
 * Why a session ended. A roll is one of the two thresholds; a run session ends with its run; `merged` is the
 * session a conversation was in when the chats before it were folded into it, which is never resumed.
 */
export type SessionEndReason = "rolled_idle" | "rolled_size" | "rolled_prompt" | "rolled_vendor" | "run_ended" | "merged";

/**
 * One stretch of a conversation held by one model provider. The conversation is kept for good; its sessions roll
 * underneath it. What the chat needs to draw a divider and show "where we left off".
 */
export type SessionDTO = {
  id: string;
  conversationId: string;
  provider: string;
  kind: "chat" | "run";
  startedAt: string | null;
  endedAt: string | null;
  endedReason: SessionEndReason | null;
  /** The note the session it picked up from left behind; null on a conversation's first session. */
  summary: string | null;
  seededFromSessionId: string | null;
  /** The first run in this session: where the chat draws the divider. */
  firstRunId: string | null;
  turns: number;
  /** The measure: the tokens one model call read at the session's first turn and at its last, and the difference. */
  firstPromptTokens: number | null;
  lastPromptTokens: number | null;
  conversationTokens: number;
};

/** Thresholds lowered for one conversation so a roll can be watched happening. Admin only; never the real ones. */
export type SessionLimits = { rollTokens?: number; idleTokens?: number; idleHours?: number };

export type MessageDTO = {
  id: string;
  conversationId: string;
  role: "user" | "assistant" | "system";
  content: string;
  runId: string | null;
  /** The sender: a person, or an agent's identity. Null for a message the platform wrote itself. */
  userId?: string | null;
  /** Who it was said to: a person, or an agent's identity. */
  recipientId?: string | null;
  /** The person the work serves; an agent's identity for work it does on its own behalf. */
  forUserId: string;
  /** The door it came through, and the one a reply to it goes back out of. */
  channel: MessageChannel;
  /** Agent-to-agent: kept out of the chat, behind an "Asked <Agent>" line. */
  internal: boolean;
  /** Where exactly a reply goes on that channel: the Slack channel and thread, the Gmail thread, the child run. */
  channelRef?: string | null;
  /** The request this message shows (the agent's card) or answers (the platform's note back to it). */
  approvalId?: string | null;
  createdAt: string;
  files?: FileDTO[];
};

export type FileDTO = {
  id: string;
  name: string;
  mime: string;
  size: number;
  sha256: string;
  userId: string;
  conversationId: string | null;
  runId: string | null;
  source: "upload" | "agent";
  /**
   * Who it belongs to (security plan D16): the identity behind the run that made it — the person it worked for, or an
   * agent's own identity — or whoever uploaded it. `userId` is only where it came from.
   */
  ownerUserId: string;
  ownerName: string | null;
  ownerKind: "person" | "agent" | "service" | null;
  visibility: FileVisibility;
  /** Kept for later runs (`pandas files keep`, or uploaded on an agent's or a scope's page), never shown in a chat. */
  kept: boolean;
  /** How many people and agents it is shared with by name. */
  sharedWith: number;
  /** The scope it was made under or kept on: every run under that scope reads it. Null otherwise. */
  subjectId: string | null;
  subjectName: string | null;
  /** The meeting it is a recording piece or a screenshot of: the meeting's material, on its page and in no Files list. */
  meetingId: string | null;
  /** Whether whoever asked may open it and change who may; on lists, which show every row's details either way. */
  canOpen?: boolean;
  canShare?: boolean;
  createdAt: string;
};

/** Who may read a file beside its owner, its scope and the people and agents it is shared with: nobody else, or everyone. */
export type FileVisibility = "private" | "everyone";
export const FILE_VISIBILITIES: FileVisibility[] = ["private", "everyone"];

/** The three groups of a person's Files page. */
export type FileGroup = "mine" | "shared" | "everyone";

/** A file's sharing, as its owner's control shows it: the same shape as a page's. */
export type FileSharingDTO = { visibility: FileVisibility; shares: PageSharee[]; candidates: PageSharee[] };

export type MeetingPlatform = "google_meet" | "zoom" | "teams" | "unknown";

/** Which calendars the meeting bot follows and which meetings it skips. */
export type MeetingCalendarSettings = {
  /** Join upcoming meetings from the watched calendars automatically. */
  autoJoin: boolean;
  /** Calendars to read (Workspace emails), e.g. the Pandas account people invite. */
  calendars: string[];
  /** Skip meetings whose title contains any of these (case-insensitive). */
  excludeKeywords: string[];
  /** Skip meetings organized by these emails or domains (e.g. "ceo@pandas.io", "@partner.com"). */
  excludeOrganizers: string[];
  /** Join meetings automatically (even with `autoJoin` off), skipping any with someone outside `internalDomains` (organizer or guests). */
  internalOnly: boolean;
  /** The company's email domains, e.g. ["pandas.io"]. */
  internalDomains: string[];
  /** Receives the minutes when the organizer has no Pandas account. */
  fallbackUserEmail: string | null;
  /**
   * After a meeting's minutes are stored, Pandito tells each Pandas person in it, by Slack DM or in their chat with it,
   * where the minutes are and their own action items (AIT-336). When off, nothing is sent; the minutes are still written
   * and kept on the meeting's page.
   */
  sendMinutes: boolean;
  /** The language Pandas sets for Meet's live captions (e.g. "Greek"); captions follow one spoken language at a time. */
  captionLanguage: string;
  /** Record the meeting audio and transcribe it with this engine (mixed languages); "captions" uses only Meet's captions. */
  transcription: MeetingTranscriptionEngine;
  /** The name Pandas joins under, shown to everyone in the call (Zoom and Teams ask a guest for it). */
  botName: string;
};

export type MeetingTranscriptionEngine = "captions" | "gemini" | "openai" | "whisper";

/** An upcoming calendar event with a meeting link. */
export type MeetingEventDTO = {
  id: string;
  calendarEmail: string;
  title: string;
  startsAt: string;
  endsAt: string;
  meetingUrl: string;
  platform: MeetingPlatform;
  organizerEmail: string | null;
  attendeeCount: number;
  /** In how many watched calendars this meeting appears. */
  calendarCount: number;
  /** The calendar description (may contain HTML). */
  description: string;
  attendees: { email: string; name: string | null; responseStatus: "accepted" | "declined" | "tentative" | "needsAction"; organizer: boolean; optional: boolean }[];
  attachments: { title: string; fileUrl: string; mimeType: string | null }[];
  /** The event in Google Calendar. */
  htmlLink: string | null;
  /** An admin's explicit choice; null follows the rules. */
  excludedOverride: boolean | null;
  excludedByRule: string | null;
  /** The effective decision. */
  excluded: boolean;
  meetingId: string | null;
  meetingStatus: MeetingStatus | null;
  joinError: string | null;
};
export type MeetingStatus = "requested" | "joining" | "waiting" | "in_call" | "processing" | "done" | "failed" | "cancelled";
export type MeetingTranscriptEntry = { speaker: string; text: string; at: string };

/**
 * Someone Google's Meet API listed in the call (security plan D17): signed in with a Google account, anonymous (a name
 * typed at the door) or a phone. A colleague's account resolves to their address and person; nobody else's does.
 */
export type MeetingParticipant = {
  /** Google's resource name for this participant in this conference. */
  key: string;
  name: string;
  kind: "signed_in" | "anonymous" | "phone";
  email: string | null;
  userId: string | null;
  joinedAt: string | null;
};

/** A meeting the "Pandas" bot was sent to, with its caption transcript and the minutes written afterwards. */
export type MeetingDTO = {
  id: string;
  url: string;
  platform: MeetingPlatform;
  title: string;
  status: MeetingStatus;
  botName: string;
  requestedByUserId: string;
  requestedByName: string | null;
  originConversationId: string | null;
  notesConversationId: string | null;
  notesRunId: string | null;
  /** Pandas Bot conversation answering questions asked during the call. */
  liveConversationId: string | null;
  notifySlack: string | null;
  /** Empty in list responses; see transcriptCount. */
  transcript: MeetingTranscriptEntry[];
  transcriptCount: number;
  /** Recorded audio chunks by transcription status. */
  audio: { chunks: number; done: number; pending: number; failed: number; skipped: number };
  /** Transcribed audio merged with captions and chat (what the minutes use); empty in list responses and without audio. */
  audioTranscript: MeetingTranscriptEntry[];
  /** The recording, in order: one WebM file per piece; empty in list responses. */
  audioChunks: { fileId: string; startedAt: string; endedAt: string; status: string }[];
  /** Set while the meeting has a public page at /m/<token>. */
  publicToken: string | null;
  publishedAt: string | null;
  /** People on the public page right now. */
  publicViewers: number;
  /** Screenshots of shared screens, in order. */
  frames: { fileId: string; at: string }[];
  /**
   * The rest of the meeting's material (`files.meeting_id`): the bot's screenshots while it asked for help joining, an
   * earlier attempt's end screenshot, a piece delivered twice. In no Files list, so listed here; empty in list responses.
   */
  otherFiles: { fileId: string; name: string; at: string }[];
  minutes: string | null;
  debugFileId: string | null;
  error: string | null;
  joinedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Everyone Google listed in a Meet call while the bot was there (D17); empty for Zoom and Teams. */
  participants: MeetingParticipant[];
  /** While the bot is in the call: when Meet's live captions stopped arriving although people talk; null while they flow. */
  captionsStoppedAt: string | null;
  /** How many people are in it (AIT-335): its members, never a guest. */
  memberCount?: number;
  /**
   * The row of a meeting the viewer is not in (AIT-335): that it happened — its times, platform, status, error and how
   * many are in it — and nothing it holds. Only its members open it, admins included.
   */
  private?: boolean;
  /** On the meeting's own page: who is in it, and the guests seen in the call or invited, by name only. */
  people?: { members: { id: string; name: string }[]; guests: string[] };
};

/**
 * One message from Pandito to a person in a meeting after its minutes are stored (AIT-336), or a send to me only
 * (`preview`): how it went, never what it said. `via`: a Slack DM from Pandito's bot, or their chat with Pandito.
 */
export type MeetingMessageDTO = {
  id: string;
  meetingId: string;
  userId: string;
  preview: boolean;
  status: "pending" | "sent" | "failed" | "skipped";
  via: "slack_dm" | "web" | null;
  /** Why it failed, was skipped, or did not go by Slack. */
  detail: string | null;
  /** How many of their own action items it listed. */
  items: number | null;
  createdAt: string;
  updatedAt: string;
};

/** Who may see a page beside its owner and the people it is shared with: nobody else, everyone on the platform, or anyone with the link. */
export type PageVisibility = "private" | "everyone" | "public";
export const PAGE_VISIBILITIES: PageVisibility[] = ["private", "everyone", "public"];

/**
 * What a page's content may do inside its frame, the same on the content's own header and on the shell's iframe: run
 * scripts, handle its own forms in script, open a link in a new tab, download; never be same-origin with anything,
 * never raise a dialog over our chrome (security plan D1).
 */
export const PAGE_SANDBOX = "allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads";

/**
 * A hosted web page (HTML/JS/CSS plus assets in folders). Its link is our own `/p/<id>`: the shell, with the content
 * framed from the pages host (security plan D1).
 */
export type PageDTO = {
  id: string;
  title: string;
  /** Brief and development notes, kept current by the agent so later runs can continue the work. */
  description: string;
  visibility: PageVisibility;
  ownerUserId: string;
  ownerName: string | null;
  ownerEmail: string | null;
  createdByAgentSlug: string | null;
  updatedByAgentSlug: string | null;
  conversationId: string | null;
  lastRunId: string | null;
  /** Absolute URL of the page. */
  url: string;
  createdAt: string;
  updatedAt: string;
};

/** What the shell at /p/<id> draws: the page, where its content is framed from, and whether this viewer may share it. */
export type PageViewDTO = {
  page: Pick<PageDTO, "id" | "title" | "description" | "visibility" | "ownerName" | "createdByAgentSlug" | "updatedAt">;
  /** The content on the pages host, signed for a few hours; null when no pages host is set up here. */
  frameUrl: string | null;
  canShare: boolean;
};

/** Someone a page or a file can be shared with: a person, or an agent's own identity. */
export type PageSharee = { userId: string; name: string; kind: "person" | "agent" };

export type PageSharingDTO = { visibility: PageVisibility; shares: PageSharee[]; candidates: PageSharee[] };

export type PageFileDTO = {
  /** Relative to the page root, e.g. "index.html" or "assets/app.js". */
  path: string;
  size: number;
  mime: string;
  updatedAt: string;
};

/** When a schedule fires: once at `runAt`, on a cron expression in a timezone, or only when its owner runs it (D12). */
export const SCHEDULE_TRIGGERS = ["once", "cron", "manual"] as const;
export type ScheduleTrigger = (typeof SCHEDULE_TRIGGERS)[number];

/** Everything an agent can be, decided in this order. See agentStatus(). */
export type AgentStatus = "disabled" | "thinking" | "waiting" | "blocked" | "failing" | "scheduled" | "sleeping";
export const AGENT_STATUSES = ["thinking", "waiting", "blocked", "failing", "scheduled", "sleeping", "disabled"] as const;

/** Where a schedule's answer goes beyond its owner's own chat (AIT-55): a person's web chat or Slack DM, or a Slack channel. */
export const SCHEDULE_DESTINATION_KINDS = ["web", "slack_dm", "slack_channel"] as const;
export type ScheduleDestinationKind = (typeof SCHEDULE_DESTINATION_KINDS)[number];
/** How one delivery of one firing went: waiting, sent, failed (tried again, three attempts in all), or skipped. */
export type ScheduleDeliveryStatus = "pending" | "sent" | "failed" | "skipped";

/** One place a schedule's answer is delivered, by the platform, from Pandito on Slack. */
export type ScheduleDestinationDTO = {
  id: string;
  kind: ScheduleDestinationKind;
  /** The person, for a web chat or a Slack DM. */
  userId: string | null;
  userName: string | null;
  /** For a Slack DM: whether the person has used Slack with Pandito, so the platform knows their Slack account. */
  linked: boolean;
  /** For a Slack channel: its id (`C…` or `G…`) and what the owner calls it. */
  channel: string | null;
  label: string | null;
  /** How the last firing's delivery here went; null before the first. */
  lastDelivery: { status: ScheduleDeliveryStatus; at: string; detail: string | null } | null;
};

/** What a save names: a person's web chat or Slack DM, or a channel by its id. The list replaces the schedule's. */
export type ScheduleDestinationInput = { kind: "web" | "slack_dm"; userId: string } | { kind: "slack_channel"; channel: string; label?: string | null };

/**
 * Whom a schedule runs as: its person (D6), or the agent itself (AIT-56), when the agent's owners gave it one of its own
 * and its tools answer with the agent's workspace keys.
 */
export type ScheduleRunsAs = "person" | "agent";

/**
 * A schedule: one agent, one prompt, one trigger (D12), a person's — created by them, or by an agent working for them —
 * and run as them, in their own conversation with the agent (D6). Its answer lands there, and the platform also
 * delivers it to the destinations its owner chose (AIT-55). Or the agent's own, given it by its owners and run on its
 * own behalf, its answer delivered only where they send it (AIT-56).
 */
export type ScheduleDTO = {
  id: string;
  slug: string;
  name: string;
  description: string;
  agentId: string;
  agentSlug: string | null;
  agentName: string | null;
  /** Who it belongs to and runs as: a person, or the agent's own identity when it runs as the agent itself. */
  ownerUserId: string | null;
  ownerName: string | null;
  runsAs: ScheduleRunsAs;
  /** For one that runs as the agent itself: the person who set it up, told when it fails while they own the agent. */
  setUpBy: string | null;
  setUpByName: string | null;
  prompt: string;
  trigger: ScheduleTrigger;
  cron: string | null;
  timezone: string | null;
  /** For a `once` schedule: when it fires. */
  runAt: string | null;
  enabled: boolean;
  /** Its answer also goes to the owner's Slack DM: read from `destinations`. */
  slackDm: boolean;
  /** Where the platform delivers its answer besides the owner's own chat. */
  destinations: ScheduleDestinationDTO[];
  /** The model its owner picked (AIT-76), or null for its agent's default, whatever that is when it fires. */
  model: string | null;
  /** Why the platform paused it — the owner lost access, left, it waits for someone to accept it — or null. */
  pausedReason: string | null;
  /** It is offered to someone and waits for their Accept (AIT-62): it does not run meanwhile. Null when it is not. */
  offer: ScheduleOfferDTO | null;
  /** The scope it is about: its run works under it. Null = about nothing in particular. */
  subjectId: string | null;
  subjectKey: string | null;
  subjectName: string | null;
  /** The run that created it, when an agent working for its owner did. */
  createdByRunId: string | null;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastRunStatus: RunStatus | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * What a schedule may be put on (AIT-76): its agent's default, always offered, and the rest of `options`, which a person's
 * logins or the agent's model list decide. `why`: in plain words, why only the default is offered.
 */
export type ScheduleModelsDTO = { defaultModel: string; options: ModelOption[]; why: string | null };

/** A schedule's open offer (AIT-62): the request that waits, whom it is to, and who offered it. */
export type ScheduleOfferDTO = { approvalId: string; toUserId: string; toName: string; byUserId: string | null; byName: string | null; at: string };

/** One run of a schedule from before 070, when a schedule was a list of steps: kept read-only (`schedule_runs_legacy`). */
export type LegacyScheduleRunDTO = {
  id: string;
  scheduleId: string | null;
  triggerType: string;
  status: RunStatus;
  conversationId: string | null;
  userId: string | null;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
};

/** Admin-only agent behind the floating assistant in the admin UI; manages platform data through the admin API and SQL. */
export const PLATFORM_ASSISTANT_SLUG = "platform-assistant";

/** Who a memory belongs to: everyone, one agent, one person, one person with one agent, or one scope. */
export type MemoryLevel = "global" | "agent" | "user" | "user_agent" | "scope";

export type MemoryDTO = {
  id: string;
  level: MemoryLevel;
  agentId: string | null;
  userId: string | null;
  /** For a scope memory (level "scope"): the scope, whose memory every agent working under it reads. */
  subjectId: string | null;
  scopeKey: string | null;
  scopeName: string | null;
  title: string;
  content: string;
  tags: string[];
  pinned: boolean;
  source: "agent" | "user" | "admin" | "system";
  createdByRun: string | null;
  /** The agent whose run wrote it, when a run did. */
  authorAgentSlug: string | null;
  /** Who last changed it, at updatedAt: a run (and its agent) or a person, never both. Both null: never changed since it was recorded. */
  updatedByRun: string | null;
  updatedByAgentSlug: string | null;
  updatedByUser: string | null;
  updatedByUserName: string | null;
  createdAt: string;
  updatedAt: string;
};

/** A named token of a person or a service account: its value is shown once and never kept. */
export type UserTokenDTO = {
  id: string;
  userId: string;
  label: string;
  /** The value's last four characters, to tell tokens apart; null for a token made before they were kept. */
  hint: string | null;
  createdBy: string | null;
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
};

export type UserDTO = {
  id: string;
  email: string;
  name: string;
  role: "admin" | "member";
  /** A person who signs in; an agent's own identity (the principal of its autonomous runs; never signs in); or a service account — an admin-API principal for a script or an automation, which never owns an agent, is never a request's addressee or decider, and holds no conversation. */
  kind: "person" | "agent" | "service";
  /** A retired user stays for history and cannot sign in. */
  retiredAt: string | null;
  /** The agent this identity belongs to, when kind is "agent". */
  agentId: string | null;
  createdAt: string;
  jobTitle: string | null;
  department: string | null;
  location: string | null;
  manager: string | null;
  githubUsername: string | null;
  /** Whether a Google identity is bound to this person; the subject itself is read by nobody but an admin in the audit log. */
  auth0Bound: boolean;
  /** Free text written by admins, e.g. "Owns EU enterprise accounts". */
  description: string;
  /** "bamboohr" when job fields are synced from BambooHR. */
  profileSource: string | null;
  profileSyncedAt: string | null;
  /** When they last signed in, and when they last did anything in the app. Null until they do. */
  lastLoginAt: string | null;
  lastSeenAt: string | null;
};

/** Someone with the app open right now. */
export type PresenceDTO = { id: string; name: string; since: string };

/** Client → server WebSocket messages. */
export type WsClientMessage =
  | { op: "subscribe"; topic: string; since?: Record<string, number> }
  | { op: "unsubscribe"; topic: string };

/** Server → client WebSocket messages. */
export type WsServerMessage =
  /** First on every connection: the platform's build, so an open page knows when a deploy has replaced it. */
  | { op: "hello"; build: string }
  | { op: "event"; topic: string; event: PlatformEvent }
  | { op: "subscribed"; topic: string }
  | { op: "error"; error: string };

/** Email to Pandas: the Pandas mailbox is read with the Google Workspace connector and answered in the same thread. */
export type EmailInboxSettings = {
  enabled: boolean;
  /** The mailbox Pandas reads and replies from, e.g. pandas@pandas.io. */
  address: string;
  /** Only people with a Pandas account on these domains get answers; everything else is ignored. */
  allowedDomains: string[];
  checkEverySeconds: number;
};

export type EmailMessageDTO = {
  id: string;
  threadId: string;
  fromEmail: string;
  fromName: string | null;
  subject: string;
  snippet: string;
  receivedAt: string | null;
  status: "pending" | "processed" | "ignored" | "failed";
  reason: string | null;
  userName: string | null;
  conversationId: string | null;
  runId: string | null;
  replyStatus: "pending" | "sent" | "failed" | null;
  replyError: string | null;
  repliedAt: string | null;
  createdAt: string;
};

export type EmailInboxView = {
  settings: EmailInboxSettings;
  connector: string | null;
  problem: string | null;
  lastCheckAt: string | null;
  lastError: string | null;
  messages: EmailMessageDTO[];
};

/** What the public meeting page at /m/<token> shows: no links, people's accounts or internal details. */
export type PublicMeetingDTO = {
  title: string;
  status: MeetingStatus;
  platform: MeetingPlatform;
  live: boolean;
  joinedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  minutes: string | null;
  /** Transcribed audio only (no live captions). */
  transcript: MeetingTranscriptEntry[];
  frames: { fileId: string; at: string }[];
  audioChunks: { fileId: string; startedAt: string; endedAt: string; status: string }[];
  viewers: number;
};

/** One browser tab that opened a public meeting page. */
export type MeetingViewerDTO = {
  viewerId: string;
  ip: string | null;
  countryCode: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  /** Network (ISP or company), e.g. "OTE SA". */
  org: string | null;
  device: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  watching: boolean;
};

/** What one person's runs cost, each run counted once: a delegated run carries the person it works for. */
export type PersonSpendDTO = CostSplit & { runs: number; costUsd: number };

/** Per-agent numbers for the agents list (last 30 days unless noted). */
export type AgentSummaryStatsDTO = CostSplit & {
  runs: number; costUsd: number; active: number; memories: number; lastRunAt: string | null;
  status: AgentStatus; schedules: number; nextRunAt: string | null; pendingApprovals: number; monthSpendUsd: number;
};

export const WISHLIST_TYPES = ["tool", "connector", "feature", "skill"] as const;
export type WishlistType = (typeof WISHLIST_TYPES)[number];
export const WISHLIST_TYPE_LABEL: Record<WishlistType, string> = { tool: "Tool", connector: "Connector", feature: "Feature", skill: "Skill" };
export type WishlistStatus = "open" | "planned" | "shipped" | "declined";
/** What an admin may say about an item: a verdict never reopens one. */
export const WISHLIST_VERDICTS = ["planned", "shipped", "declined"] as const;
export type WishlistVerdict = (typeof WISHLIST_VERDICTS)[number];
/** The list's views: what is still alive, or one of the two endings. */
export const WISHLIST_VIEWS = ["active", "shipped", "declined"] as const;
export type WishlistView = (typeof WISHLIST_VIEWS)[number];

/**
 * One thing that has to be built, as the Wishlist page lists it. A title, a brief, a reason and a vote's note are
 * people's and agents' own words, so never rendered as anything but text.
 */
export type WishlistItemDTO = {
  id: string;
  type: WishlistType;
  title: string;
  brief: string;
  status: WishlistStatus;
  /** Why it was declined. */
  reason: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  askedByName: string | null;
  /** The platform found it in the runs; with no asker yet, nobody has filed it. */
  noticed: boolean;
  /** The agent that will hold a planned skill. */
  agentId: string | null;
  agentSlug: string | null;
  agentName: string | null;
  votes: number;
  /** Distinct agents and runs behind it: "needed by 4 agents across 31 runs". */
  agents: number;
  runs: number;
  youVoted: boolean;
  createdAt: string;
};

export type WishlistVoterDTO = { id: string; name: string; note: string | null; at: string };

/** What a run needed, as an admin reads it: a run's words can carry what it was working on, so a member gets none of it. */
export type WishlistEvidenceDTO = {
  id: string; runId: string; agentName: string | null; source: "reported" | "noticed"; reason: string | null; command: string | null; at: string;
};

/** One item opened: everyone reads who voted and their notes, an admin also reads the runs behind it. */
export type WishlistDetailDTO = WishlistItemDTO & {
  voters: WishlistVoterDTO[];
  evidence: WishlistEvidenceDTO[];
  /** This viewer may open the Draft chat: a real admin who may use the agent that will hold a planned skill. */
  canDraft: boolean;
};

/** The agent page: activity, spend, delegations, tool and connector usage, memory and schedules. */
export type AgentInsightsDTO = {
  agent: AgentDTO;
  status: AgentStatus;
  /** When its first enabled schedule fires next, drawn beside "Scheduled". */
  nextRunAt: string | null;
  days: number;
  stats: CostSplit & {
    runs: number; succeeded: number; failed: number; active: number; costUsd: number; costMonthUsd: number;
    /** `costMonthUsd`, split the same way. */
    month: CostSplit;
    inputTokens: number; outputTokens: number; avgDurationMs: number | null;
  };
  daily: (CostSplit & { day: string; runs: number; costUsd: number })[];
  tools: { name: string; calls: number }[];
  connectors: { slug: string; name: string; calls: number; errors: number }[];
  delegatedTo: (CostSplit & { agentId: string; slug: string; name: string; runs: number; costUsd: number })[];
  delegatedFrom: { agentId: string; slug: string; name: string; runs: number }[];
  recentRuns: RunDTO[];
  memories: MemoryDTO[];
  schedules: ScheduleDTO[];
  goals: AgentGoalDTO[];
  approvals: ApprovalDTO[];
  parent: AgentDTO | null;
  subAgents: AgentDTO[];
  monthSpendUsd: number;
};

export type AgentGoalStatus = "active" | "achieved" | "paused" | "dropped";

/** How much of a goal's description every run reads (AIT-31): the Goal panel warns above it, and never refuses. */
export const GOAL_DESCRIPTION_BUDGET = 3500;

/** An agent's goal: one current (active or paused) at most, the rest history. It has no due date (093). */
export type AgentGoalDTO = {
  id: string;
  agentId: string;
  title: string;
  description: string;
  /** What "achieved" means, measured outside the agent: the screens and the prompt say "Achieved when". */
  target: string;
  status: AgentGoalStatus;
  createdByType: "user" | "agent";
  createdAt: string;
  updatedAt: string;
};

/** `task` is history only: no new task proposal since 2026-10-18 (D7) and no task feature since 2026-10-19; the 5 decided ones still draw. */
export type ApprovalKind = "goal" | "task" | "workflow" | "schedule" | "sub_agent" | "question" | "note" | "skill" | "review" | "file_share" | "agent_edit" | "skill_change" | "schedule_handover" | "access" | "action";

/**
 * What an `action` request carries (AIT-108): the exact call, as the agent proposed it and as the platform sends it.
 * The body is its bytes in base64, so nothing about it can change on the way into the row or out of it; the card is
 * drawn from these bytes, never from anything the agent said about them.
 */
export type ActionCall = {
  connector: string;
  connectorName: string;
  /** The rule's label when it was filed. */
  label: string;
  display: ActionDisplay;
  method: string;
  /** As proposed: relative to the connector's base URL, with no query. */
  path: string;
  /** "?a=b", or "" with no query. */
  query: string;
  contentType: string | null;
  /** Null for a call with no body. */
  bodyBase64: string | null;
  bytes: number;
};

/** What a `skill_change` request does to a skill that is already written (AIT-51). */
export type SkillChangeAction = "update" | "archive" | "share" | "unshare";

/** One file of a proposed skill version against the current one: text with both sides to diff, anything else by size. */
export type SkillFileChange = {
  path: string;
  change: "added" | "removed" | "changed";
  /** True when either side is not UTF-8 text: drawn as its name and size only. */
  binary: boolean;
  before?: string;
  after?: string;
  beforeSize?: number;
  afterSize?: number;
  /** Set when only, or also, whether it may be run changed. */
  executable?: { before: boolean; after: boolean };
};

/**
 * What a `skill_change` request carries: the skill, what it was proposed against (the stale guard compares `base` at
 * the approval), and the change itself — a new folder with every file's change, archiving, or the agents that gain or
 * lose it. Built by the platform from the skill as it stood, never taken from the run's word.
 */
export type SkillChangePayload = {
  action: SkillChangeAction;
  skillId: string;
  name: string;
  level: "person" | "agent";
  base: { versionId: string | null; version: number | null; allAgents: boolean; agentIds: string[] };
  /** Who has it as proposed against: the agents' names, or "every agent". */
  usedBy: string[];
  files?: { path: string; base64: string; executable: boolean }[];
  description?: { before: string; after: string };
  changes?: SkillFileChange[];
  next?: { allAgents: boolean; agentIds: string[] };
  gains?: string[];
  loses?: string[];
};

/**
 * What an `agent_edit` request carries: each part it changes, with the text it was proposed against. A restore the
 * Platform Assistant asked for (AIT-52) names the version whose instructions it brings back.
 */
export type AgentEditPayload = {
  instructions?: { before: string; after: string; baseVersionId: string | null; baseVersion: number | null; restoredFrom?: number };
  description?: { before: string; after: string };
};

/**
 * What a `schedule_handover` request carries (AIT-62): who offers it, and the schedule as it was offered, so the card
 * says exactly what the person takes over; `before` is the on/off state a decline or a withdrawal puts back.
 */
export type ScheduleHandoverPayload = {
  scheduleId: string;
  name: string;
  prompt: string;
  agentName: string;
  trigger: ScheduleTrigger;
  cron: string | null;
  timezone: string | null;
  runAt: string | null;
  /** Whom it ran as when it was offered: a person, or the agent itself. */
  runsAs: ScheduleRunsAs;
  /** Where its answer goes besides its owner's own chat, each with its words ("Fanis (Slack DM)", "#product (Slack channel)"). */
  destinations: { kind: ScheduleDestinationKind; userId: string | null; words: string }[];
  /** The model it was picked to run on, or null for its agent's default (AIT-76); an offer from before it has none. */
  model?: string | null;
  offeredBy: { id: string; name: string };
  before: { enabled: boolean; pausedReason: string | null };
};

/**
 * What an `access` request asks for (AIT-105): three for the person the run works for, three for an agent. Becoming an
 * admin or owning an agent is not among them, and neither are scope types or review types, which are an owner's design call.
 */
export const ACCESS_TYPES = ["agent_use", "connector_use", "connector_level", "agent_connector", "agent_connector_level", "agent_delegate"] as const;
export type AccessType = (typeof ACCESS_TYPES)[number];
/** The types an agent's owners decide; any real admin decides the other two, a person's connector and its level. */
export const OWNERS_DECIDE: AccessType[] = ["agent_use", "agent_connector", "agent_connector_level", "agent_delegate"];

/** A connector as an access request names it. `ownLogin`: it works for a person only once they connect their own account. */
export type AccessConnector = { id: string; slug: string; name: string; ownLogin: boolean };

/**
 * What an `access` request carries, built by the platform from the run and never taken from its word. `key` is the
 * thing asked for: one card at a time per key. `missing` is what `agent_use` also grants, the card's list and no other.
 */
export type AccessPayload = {
  type: AccessType;
  key: string;
  /** The person it is for, on a person's type: the person the run works for, or whoever the Platform Assistant names. */
  person?: { id: string; name: string };
  /** The admin it was filed for, when the Platform Assistant filed it: its card says so (AIT-112). */
  askedBy?: { id: string; name: string };
  /** The agent it is for, or the agent `agent_use` opens. */
  agent?: { id: string; slug: string; name: string };
  connector?: AccessConnector;
  level?: { key: string; name: string };
  /** The level they are on now, for the card. */
  from?: { key: string; name: string };
  missing?: AccessConnector[];
  /** Set by the approval: everything asked for was held by then, so nothing was written. */
  alreadyHeld?: boolean;
};

/** What an access request says after the agent's name: the card and the record both read it. */
export const asksForAccess = (p: Pick<AccessPayload, "askedBy">) => `asks for access${p.askedBy ? ` on behalf of ${p.askedBy.name}` : ""}`;

/** What a proposal wants, in words: "<Agent> proposes to <this>". The card and the record both read it. */
export const PROPOSES: Record<ApprovalKind, string> = {
  goal: "set its goal",
  task: "create a task (retired)",
  workflow: "set up a schedule (the old kind)",
  schedule: "set up a schedule",
  sub_agent: "create a sub-agent",
  question: "ask a question",
  note: "add this to your note",
  skill: "add a skill",
  review: "have this reviewed before it is used",
  file_share: "share a file",
  agent_edit: "change its own instructions or description",
  skill_change: "change a skill",
  schedule_handover: "hand over a schedule",
  access: "ask for access",
  action: "take an action that needs a person's approval",
};

/** How a skill change reads after "<Agent> proposes to": the action, and a person's own skill says whose it is. */
const SKILL_CHANGE: Record<SkillChangeAction, string> = {
  update: "save a new version of",
  archive: "archive",
  share: "give more agents",
  unshare: "take some agents off",
};

/**
 * What a request proposes, in the words its card and the chat's record use: a skill says whom it is for — the one
 * person it is addressed to, or everyone the agent works with (its owners decide it).
 */
export function proposesText(a: { kind: ApprovalKind; ownerUserId: string | null; ownerName?: string | null; payload?: Record<string, unknown>; targetAgentName?: string | null }): string {
  // An agent's own edit says which of the two it changes, read from the payload's keys; a reader without the payload
  // gets the plain words.
  // A change the Platform Assistant files for another agent names that agent (AIT-52).
  if (a.kind === "agent_edit") {
    const p = a.payload ?? {};
    const parts = [p.instructions && "instructions", p.description && "description"].filter(Boolean);
    const whose = a.targetAgentName ? `${a.targetAgentName}'s` : "its own";
    return parts.length ? `change ${whose} ${parts.join(" and ")}` : a.targetAgentName ? `change ${whose} instructions or description` : PROPOSES.agent_edit;
  }
  if (a.kind === "skill_change") {
    const action = SKILL_CHANGE[a.payload?.action as SkillChangeAction];
    const whose = a.ownerUserId ? `${a.ownerName ?? "one person"}'s own skill` : "one of its skills";
    return action ? `${action} ${whose}` : `${PROPOSES.skill_change}: ${whose}`;
  }
  if (a.kind !== "skill") return PROPOSES[a.kind];
  return `${PROPOSES.skill} ${a.ownerUserId ? `for ${a.ownerName ?? "one person"}` : "for everyone it works with"}`;
}

/** One choice on a question. "Other" is implied on every choice question and is never one of these. */
export type QuestionOption = {
  key: string;
  label: string;
  description?: string;
};

/** One question of an ask. No options means free text, answered by the person's next message in the chat. */
export type ApprovalQuestion = {
  id: string;
  question: string;
  /** Two or three words above the options, as a title. */
  header?: string;
  /** Checkboxes and a Submit button instead of one click. */
  multiSelect?: boolean;
  options?: QuestionOption[];
};

/** What one question was answered with: the keys picked (plus anything typed under "Other"), or free text. */
export type QuestionAnswer = {
  keys?: string[];
  other?: string | null;
  text?: string;
};

export const MAX_QUESTIONS = 4;
export const MAX_QUESTION_OPTIONS = 8;

/** One answer the agent offers its owner. `apply` creates what was proposed; `decline` does not. */
export type ApprovalOption = {
  key: string;
  label: string;
  description?: string;
  effect?: "apply" | "decline";
};

/** Where a request stands. "revision_requested" is a review's third verdict: decided, and the agent files version N+1. */
/**
 * `withdrawn`: the agent took it back, its addressee withdrew an approved item that was never sent, or the platform
 * closed it — the person it waited for left, or may no longer make the send it is for.
 */
export type ApprovalStatus = "pending" | "approved" | "rejected" | "answered" | "revision_requested" | "withdrawn";

/** One card on the Review Pages tab: a template, how many of its items wait for this person, and the first one. */
export type ReviewPageDTO = {
  template: string;
  label: string;
  route: string;
  waiting: number;
  preview: string | null;
  /** The agents granted this template in their settings, by name: the ones that may file it. */
  agents: { id: string; name: string }[];
};

/** One version of a reviewed item: its own row, what was filed and how it was decided. */
export type ReviewVersionDTO = {
  id: string;
  version: number;
  status: ApprovalStatus;
  filedAt: string;
  decidedAt: string | null;
  decidedByName: string | null;
  decisionNote: string | null;
  /** What was filed, so an older version can be read as it was. Empty for a reader who may not read the item. */
  payload: Record<string, unknown>;
};

/**
 * What SalesOS says about the people in an outreach message right now, read by the platform with the `salesos`
 * connector's own key. `available` false means the tool could not be read: the page draws the message anyway.
 */
export type ReviewSalesosDTO = {
  available: boolean;
  /** Why there is nothing, in plain words, when `available` is false. */
  unavailable: string | null;
  company: {
    name: string;
    logoUrl: string | null;
    status: string | null;
    linkedinUrl: string | null;
    doNotContact: boolean;
  } | null;
  contact: {
    name: string;
    title: string | null;
    email: string | null;
    emailVerified: boolean;
    linkedinUrl: string | null;
    /** Null while SalesOS serves a face to a signed-in person only: the page draws initials. */
    photoUrl: string | null;
    doNotContact: boolean;
  } | null;
  /** Where we are with this person: what was sent, what came back. */
  state: {
    sent: number;
    lastSentAt: string | null;
    lastReplyAt: string | null;
    replySentiment: string | null;
    bounced: boolean;
  } | null;
};

/** Everything a review page needs beside the row itself: the item's other versions, and the live picture. */
export type ReviewContextDTO = {
  versions: ReviewVersionDTO[];
  salesos: ReviewSalesosDTO | null;
};

/** Whether a screen may offer a mic: the platform's transcriber, and what to say instead when there is none. */
export type TranscribeStatusDTO = {
  available: boolean;
  engine: MeetingTranscriptionEngine | null;
  why: string | null;
  maxSeconds: number;
  maxBytes: number;
};

/** The three verdicts of every review request, fixed in code: an agent never offers its own. */
export const REVIEW_DECISIONS = ["approve", "reject", "revise"] as const;
export type ReviewDecision = (typeof REVIEW_DECISIONS)[number];

/** Something an agent wants to start on its own, waiting for its owner's decision. */
export type ApprovalDTO = {
  id: string;
  kind: ApprovalKind;
  status: ApprovalStatus;
  agentId: string;
  agentSlug: string;
  agentName: string;
  /** The one person it is addressed to, who alone decides it; null means the agent's owners, any current one of whom decides (D14). */
  ownerUserId: string | null;
  ownerName: string | null;
  /**
   * The agent an `agent_edit` changes when it is not the one asking: the Platform Assistant's card for another agent
   * (AIT-52), decided by that agent's owners. Null (or absent) when a request is about the agent that filed it.
   */
  targetAgentId?: string | null;
  targetAgentName?: string | null;
  /** Addressed to any real admin rather than to one person or an agent's owners (AIT-105): `waitingFor` lists them. */
  anyAdmin?: boolean;
  /** The schedule a `schedule_handover` hands over (AIT-62); null once that schedule is deleted. */
  scheduleId?: string | null;
  /**
   * Who it is still waiting for: that person, or the agent's owners. Once decided, empty — except an approved item
   * whose send did not go (who may send it again or withdraw it) or may have gone (its addressee and the agent's
   * owners, until one of them has checked the tool).
   */
  waitingFor: { id: string; name: string }[];
  title: string;
  reason: string;
  payload: Record<string, unknown>;
  /** What the person may choose. Empty means plain approve or reject. */
  options: ApprovalOption[];
  /** A question's questions, up to four. Empty on a proposal and on asks from before the cards. */
  questions: ApprovalQuestion[];
  /** What has been answered so far, keyed by question id; a question is pending until every one of them is in here. */
  answers: Record<string, QuestionAnswer>;
  /** The chat it was asked in, where its card lives. */
  conversationId: string | null;
  /** The option the person picked, if the agent offered any. */
  decisionKey: string | null;
  /** Set once the answer has been handed back to the agent that asked. */
  answerDeliveredAt: string | null;
  resultId: string | null;
  requestedByRunId: string | null;
  decidedByUserId: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  error: string | null;
  /** On a review: which template the item is, so a page and a card know how to draw it. */
  template: string | null;
  /** The items filed together. A single item is a batch of one, and the agent is woken once the batch is decided. */
  batchId: string;
  /** The row this one revises, and which version of that item this is (1, 2, …). */
  revisionOf: string | null;
  version: number;
  /** Set when a protected action spent this approval, with the upstream id it created (slice 12b). */
  usedAt: string | null;
  usedRef: string | null;
  /** Where a person opens what it became, when the tool gives a link (Slack's permalink to the message sent). */
  usedLink: string | null;
  /** The tool refused the platform's send of this approved item: approved, not sent, and the addressee may send it again or withdraw it. */
  releaseError: string | null;
  /** The tool failed in a way that may have sent it: spent, shown as "may have been sent — check the tool". */
  releaseUncertain: boolean;
  /** The platform's send of it is under way this moment: not settled yet, whatever `usedAt` says. */
  releaseStartedAt: string | null;
  /** Someone who may settle it looked in the tool after it may have been sent, and said so: it waits for nobody now. */
  releaseCheckedAt: string | null;
  releaseCheckedByName: string | null;
  createdAt: string;
};

/**
 * Where a skill applies: to one person (shared with the agents they choose), to one scope, or to agents. "Shared"
 * is not a level: a person's skill and an agent's skill alike reach agents through grants or `allAgents`.
 */
export type SkillLevel = "person" | "scope" | "agent";

/** The order a run reads the levels in: the most specific name wins. */
export const SKILL_LEVEL_RANK: Record<SkillLevel, number> = { person: 0, scope: 1, agent: 2 };

/** A procedure an agent follows, kept as a folder of files and versioned append-only. */
export type SkillDTO = {
  id: string;
  name: string;
  level: SkillLevel;
  /** Set only on a person's skill: the one person it belongs to, whose runs see it. */
  userId: string | null;
  userName: string | null;
  /** Whether any run is offered it: false when it is archived, and when it reaches no agent at all. */
  offered: boolean;
  /** Set only on a scope's skill: the scope, offered to every agent working under it. */
  subjectId: string | null;
  scopeKey: string | null;
  scopeName: string | null;
  /** The scope type's name, as an admin wrote it on the Scopes page: "Account" for account:<id>. */
  scopeTypeName: string | null;
  /** A person's or an agent's skill given to every agent, now and in future. Never a scope's. */
  allAgents: boolean;
  /** The agents it is attached to one by one: who has it. Always empty on a scope's skill. */
  grantedAgentIds: string[];
  /** The one line agents see when they wake, from the current version. */
  description: string;
  version: number;
  currentVersionId: string | null;
  fileCount: number;
  /** Total bytes of the current version's files. */
  size: number;
  /** How many times a run was handed the full text. */
  reads: number;
  lastReadAt: string | null;
  archivedAt: string | null;
  /** Who saved version 1. */
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};

/** One edition of a skill. Appended on every save, never changed or deleted. */
export type SkillVersionDTO = {
  id: string;
  version: number;
  description: string;
  /** user:<id>, agent:<run id> or system. */
  createdBy: string;
  createdAt: string;
};

/** One file of a version. `text` is null when the bytes are not valid UTF-8. */
export type SkillFileDTO = {
  path: string;
  size: number;
  executable: boolean;
  text: string | null;
};

export type SkillDetailDTO = SkillDTO & {
  versions: SkillVersionDTO[];
  files: SkillFileDTO[];
  /** Header keys of the saved SKILL.md that the platform dropped, reported back once after a save. */
  ignoredHeaderKeys?: string[];
  /** Every [[name]] in SKILL.md, with the skill it resolves to from where this one applies, or null. */
  references: { name: string; skillId: string | null }[];
  referencedBy: { id: string; name: string }[];
  /** The last 20 times a run was handed the full text. */
  recentReads: { readAt: string; agentSlug: string | null; scopeKey: string | null; runId: string | null; version: number }[];
};

/** One line of the Skills section: what a run is told it has, before it loads anything. */
export type RunSkill = {
  name: string;
  description: string;
  level: SkillLevel;
  /** The scope key of a scope's skill; null on the other levels. */
  scopeKey: string | null;
  version: number;
  files: number;
};

// ---- the person's note

/**
 * How much room a person's note has, over both of its parts together: roughly a page, about 1.5K tokens. A constant
 * until there is a global settings page; the server refuses writes past it and the size meter reads the same number.
 */
export const NOTE_MAX_CHARS = 6000;

/** How an item got into the learned part of a note. */
export type NoteOrigin = "told" | "own" | "suggested";

/** One thing an agent learned about working with a person, which the person can edit or delete. */
export type NoteItemDTO = {
  id: string;
  userId: string;
  text: string;
  origin: NoteOrigin;
  agentId: string | null;
  agentSlug: string | null;
  agentName: string | null;
  runId: string | null;
  approvalId: string | null;
  createdAt: string;
  updatedAt: string;
};

/** One save of the part a person writes themselves. The newest is current. */
export type NoteVersionDTO = {
  id: string;
  userId: string;
  text: string;
  savedByUserId: string | null;
  savedByName: string | null;
  /** The version this one is a copy of, when it was brought back from the history. */
  restoredFromId: string | null;
  createdAt: string;
};

/** A person's whole note: what they wrote, what agents learned, and how much room is left. */
export type NoteDTO = {
  userId: string;
  userName: string;
  /** The current version's text; "" when they have never written one. */
  text: string;
  currentVersionId: string | null;
  updatedAt: string | null;
  items: NoteItemDTO[];
  versions: number;
  /** Both parts together, against NOTE_MAX_CHARS. */
  chars: number;
  maxChars: number;
};
